import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAdd,
  faEdit,
  faTrash,
  faTags,
  faSearch,
  faClose,
  faSpinner,
  faCheckCircle,
  faCancel,
} from "@fortawesome/free-solid-svg-icons";
import useSWR, { mutate } from "swr";
import DashboardContenLeftMenus from "./DashboardContenLeftMenus";
import DashboardContentHeader from "./DashboardContentHeader";
import DashboardContentFooter from "./DashboardContentFooter";
import TopActionMenus from "./TopActionMenus";
import MessageModal from "./MessageModal";
import { useNavigationEvent } from "navigation-react";

// Fetcher for SWR
const fetcher = async (url) => {
  const response = await fetch(url, {
    credentials: "include",
    headers: {
      Accept: "application/vnd.api+json",
    },
  });

  if (!response.ok) {
    throw new Error(`API request failed with status ${response.status}`);
  }

  return response.json();
};

export default function CategoryRegister() {
    const { stateNavigator, data } = useNavigationEvent();

  const [searchTerm, setSearchTerm] = useState("");
  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
  const [showEditCategoryModal, setShowEditCategoryModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [addingCategory, setAddingCategory] = useState(false);
  const [loading, setLoading] = useState(false);
  const [modalSuccessMsg, setModalSuccessMsg] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [modalError, setModalError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [editCategoryForm, setEditCategoryForm] = useState({
    id: null,
    name: "",
  });

  // Fetch categories data
  const { data: categoryData, mutate: mutateCategories } = useSWR(
    `${process.env.REACT_APP_CATEGORY_API}?sort=name&page[limit]=1000`,
    fetcher,
  );

  const fetchCsrfToken = async () => {
    const response = await fetch(
      `${process.env.REACT_APP_BASE_URL}/session/token`,
      {
        method: "GET",
        credentials: "include",
      },
    );

    if (!response.ok) {
      throw new Error("Failed to fetch CSRF token");
    }

    return await response.text();
  };

  // Filter categories
  const filteredCategories = () => {
    if (!categoryData?.data) return [];

    let filtered = categoryData.data.filter((category) => {
      const searchLower = searchTerm.toLowerCase();
      return category.attributes.name?.toLowerCase().includes(searchLower);
    });

    return filtered;
  };

  // Pagination
  const categories = filteredCategories();
  const totalPages = Math.ceil(categories.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedCategories = categories.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  // Function to add a new category
  const handleAddCategory = async (e) => {
    e.preventDefault();

    if (!newCategoryName.trim()) {
      setErrorMessage("Category name is required");
      setModalError(true);
      return;
    }

    setAddingCategory(true);

    let csrfToken;
    try {
      csrfToken = await fetchCsrfToken();
    } catch (error) {
      console.error("Failed to fetch CSRF token:", error);
      setErrorMessage("Authentication error. Please refresh the page.");
      setModalError(true);
      setAddingCategory(false);
      return;
    }

    const addCategoryPayload = {
      data: {
        type: "taxonomy_term--category",
        attributes: {
          name: newCategoryName,
        },
      },
    };

    try {
      const response = await fetch(`${process.env.REACT_APP_CATEGORY_API}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/vnd.api+json",
          Accept: "application/vnd.api+json",
          "X-CSRF-Token": csrfToken,
        },
        credentials: "include",
        body: JSON.stringify(addCategoryPayload),
      });

      if (response.ok) {
        const result = await response.json();
        console.log("Category created successfully:", result);

        if (mutateCategories) {
          mutateCategories();
        }

        setNewCategoryName("");
        setShowAddCategoryModal(false);
        setSuccessMessage("Category added successfully!");
        setModalSuccessMsg(true);
      } else {
        const errorData = await response.json();
        let errorMessageText = response.statusText;
        if (errorData.errors?.[0]?.detail) {
          errorMessageText = errorData.errors[0].detail;
        }
        console.error("Category creation error:", errorData);
        setErrorMessage(`Failed to create category: ${errorMessageText}`);
        setModalError(true);
      }
    } catch (err) {
      console.error("Error creating category:", err);
      setErrorMessage(
        err.message || "An error occurred while creating the category",
      );
      setModalError(true);
    }
    setAddingCategory(false);
  };

  // Function to update category
  const handleUpdateCategory = async (e) => {
    e.preventDefault();

    if (!editCategoryForm.name.trim()) {
      setErrorMessage("Category name is required");
      setModalError(true);
      return;
    }

    setLoading(true);

    let csrfToken;
    try {
      csrfToken = await fetchCsrfToken();
    } catch (error) {
      console.error("Failed to fetch CSRF token:", error);
      setErrorMessage("Authentication error. Please refresh the page.");
      setModalError(true);
      setLoading(false);
      return;
    }

    const updateCategoryPayload = {
      data: {
        type: "taxonomy_term--category",
        id: editCategoryForm.id,
        attributes: {
          name: editCategoryForm.name,
        },
      },
    };

    try {
      const response = await fetch(
        `${process.env.REACT_APP_CATEGORY_API}/${editCategoryForm.id}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/vnd.api+json",
            Accept: "application/vnd.api+json",
            "X-CSRF-Token": csrfToken,
          },
          credentials: "include",
          body: JSON.stringify(updateCategoryPayload),
        },
      );

      if (response.ok) {
        const result = await response.json();
        console.log("Category updated successfully:", result);

        if (mutateCategories) {
          mutateCategories();
        }

        setShowEditCategoryModal(false);
        setEditCategoryForm({
          id: null,
          name: "",
        });
        setSuccessMessage("Category updated successfully!");
        setModalSuccessMsg(true);
      } else {
        const errorData = await response.json();
        let errorMessageText = response.statusText;
        if (errorData.errors?.[0]?.detail) {
          errorMessageText = errorData.errors[0].detail;
        }
        console.error("Category update error:", errorData);
        setErrorMessage(`Failed to update category: ${errorMessageText}`);
        setModalError(true);
      }
    } catch (err) {
      console.error("Error updating category:", err);
      setErrorMessage(
        err.message || "An error occurred while updating the category",
      );
      setModalError(true);
    }
    setLoading(false);
  };

  // Function to delete category
  const handleDeleteCategory = async (categoryId, categoryName) => {
    if (
      window.confirm(
        `Are you sure you want to delete category "${categoryName}"?`,
      )
    ) {
      let csrfToken;
      try {
        csrfToken = await fetchCsrfToken();
      } catch (error) {
        console.error("Failed to fetch CSRF token:", error);
        setErrorMessage("Authentication error. Please refresh the page.");
        setModalError(true);
        return;
      }

      try {
        const response = await fetch(
          `${process.env.REACT_APP_CATEGORY_API}/${categoryId}`,
          {
            method: "DELETE",
            headers: {
              "Content-Type": "application/vnd.api+json",
              "X-CSRF-Token": csrfToken,
            },
            credentials: "include",
          },
        );

        if (response.ok) {
          setSuccessMessage("Category deleted successfully!");
          setModalSuccessMsg(true);
          mutateCategories();
        } else {
          const errorData = await response.json().catch(() => ({}));
          let errorMessageText = response.statusText;
          if (errorData.errors?.[0]?.detail) {
            errorMessageText = errorData.errors[0].detail;
          }
          setErrorMessage(`Failed to delete category: ${errorMessageText}`);
          setModalError(true);
        }
      } catch (err) {
        console.error("Error deleting category:", err);
        setErrorMessage("An error occurred while deleting the category");
        setModalError(true);
      }
    }
  };

  // Calculate statistics
  const calculateStatistics = () => {
    if (!categoryData?.data) return { totalCategories: 0 };

    return { totalCategories: categoryData.data.length };
  };

  const { totalCategories } = calculateStatistics();

  return (
    <div className="dashboard-container">
      <DashboardContentHeader />
      <div className="content-wrapper">
        <DashboardContenLeftMenus />
        <div className="categories-container" style={styles.container}>
          <TopActionMenus
            AddTitle="Category Register"
            buttonLabel="Add Category"
            icon={faAdd}
            onClick={() => setShowAddCategoryModal(true)}
            backOnClick={() => stateNavigator.navigate("productregister")}
            backOnClickLabel="Product Register"
          />

          {/* STATISTICS */}
          <div style={styles.statisticsGrid}>
            <div style={styles.statCard}>
              <FontAwesomeIcon icon={faTags} style={styles.statIcon} />
              <div>
                <h3 style={styles.statValue}>{totalCategories}</h3>
                <p style={styles.statLabel}>Total Categories</p>
              </div>
            </div>
          </div>

          {/* SEARCH BAR */}
          <div style={styles.searchBar}>
            <div style={styles.searchContainer}>
              <FontAwesomeIcon icon={faSearch} style={styles.searchIcon} />
              <input
                type="text"
                placeholder="Search categories by name..."
                style={styles.searchInput}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* CATEGORIES LIST TABLE */}
          <div style={styles.card}>
            <div style={styles.tableContainer}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th>Category Name</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {!categoryData ? (
                    <tr>
                      <td
                        colSpan="2"
                        style={{ textAlign: "center", padding: "40px" }}
                      >
                        <FontAwesomeIcon icon={faSpinner} spin /> Loading
                        categories...
                      </td>
                    </tr>
                  ) : paginatedCategories.length === 0 ? (
                    <tr>
                      <td
                        colSpan="2"
                        style={{ textAlign: "center", padding: "40px" }}
                      >
                        {searchTerm
                          ? "No categories match your search"
                          : "No categories found"}
                      </td>
                    </tr>
                  ) : (
                    paginatedCategories.map((category) => (
                      <tr key={category.id}>
                        <td>
                          <div style={styles.categoryNameCell}>
                            {category.attributes.name}
                          </div>
                        </td>
                        <td style={styles.actionBTN}>
                          <button
                            style={styles.editButton}
                            onClick={() => {
                              setEditCategoryForm({
                                id: category.id,
                                name: category.attributes.name || "",
                              });
                              setShowEditCategoryModal(true);
                            }}
                            title="Edit Category"
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </button>
                          <button
                            style={styles.deleteButton}
                            onClick={() =>
                              handleDeleteCategory(
                                category.id,
                                category.attributes.name,
                              )
                            }
                            title="Delete Category"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Add Category Modal */}
            {showAddCategoryModal && (
              <div className="modal">
                <div className="overlay"></div>
                <div className="modal-content" style={{ maxWidth: "400px" }}>
                  <div className="modal-body">
                    <div className="modal-header">
                      <p>Add New Category</p>
                      <button
                        className="close-modal"
                        onClick={() => {
                          setShowAddCategoryModal(false);
                          setNewCategoryName("");
                        }}
                      >
                        <FontAwesomeIcon icon={faClose} />
                      </button>
                    </div>

                    <form
                      onSubmit={handleAddCategory}
                      className="edit-patient-form"
                    >
                      <div className="form-group">
                        <label>Category Name *</label>
                        <input
                          type="text"
                          placeholder="Enter Category Name"
                          value={newCategoryName}
                          onChange={(e) => setNewCategoryName(e.target.value)}
                          required
                          autoFocus
                        />
                      </div>

                      <div className="submitGroup">
                        <button
                          type="submit"
                          disabled={addingCategory}
                          style={styles.button}
                        >
                          {addingCategory ? (
                            <FontAwesomeIcon icon={faSpinner} spin />
                          ) : (
                            "Add Category"
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            )}

            {/* Edit Category Modal */}
            {showEditCategoryModal && (
              <div className="modal">
                <div className="overlay"></div>
                <div className="modal-content" style={{ maxWidth: "400px" }}>
                  <div className="modal-body">
                    <div className="modal-header">
                      <p>Edit Category</p>
                      <button
                        className="close-modal"
                        onClick={() => {
                          setShowEditCategoryModal(false);
                          setEditCategoryForm({
                            id: null,
                            name: "",
                          });
                        }}
                      >
                        <FontAwesomeIcon icon={faClose} />
                      </button>
                    </div>

                    <form
                      onSubmit={handleUpdateCategory}
                      className="edit-patient-form"
                    >
                      <div className="form-group">
                        <label>Category Name *</label>
                        <input
                          type="text"
                          placeholder="Enter Category Name"
                          value={editCategoryForm.name}
                          onChange={(e) =>
                            setEditCategoryForm({
                              ...editCategoryForm,
                              name: e.target.value,
                            })
                          }
                          required
                          autoFocus
                        />
                      </div>

                      <div className="submitGroup">
                        <button
                          type="submit"
                          disabled={loading}
                          style={styles.button}
                        >
                          {loading ? (
                            <FontAwesomeIcon icon={faSpinner} spin />
                          ) : (
                            "Update Category"
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            )}

            {/* PAGINATION */}
            {totalPages > 1 && (
              <div style={styles.pagination}>
                <button
                  style={styles.paginationButton}
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                <div style={styles.pageNumbers}>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        style={{
                          ...styles.pageButton,
                          ...(currentPage === pageNum
                            ? styles.activePageButton
                            : {}),
                        }}
                        onClick={() => setCurrentPage(pageNum)}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                <button
                  style={styles.paginationButton}
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
                <div style={styles.pageInfo}>
                  Page {currentPage} of {totalPages}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <DashboardContentFooter />

      {modalSuccessMsg && (
        <MessageModal
          leftIcon={faCheckCircle}
          color="green"
          message={successMessage}
          onClose={() => setModalSuccessMsg(false)}
          rightIcon={faClose}
        />
      )}
      {modalError && (
        <MessageModal
          leftIcon={faCancel}
          color="red"
          message={errorMessage}
          onClose={() => setModalError(false)}
          rightIcon={faClose}
        />
      )}
    </div>
  );
}

// Styles
const styles = {
  container: {
    width: "100%",
    height: "100%",
    backgroundColor: "#ecf2fa",
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
    padding: "34px 32px 4rem 32px",
  },
  card: {
    background: "#fff",
    padding: "25px",
    borderRadius: "12px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    width: "100%",
    marginTop: "20px",
  },
  tableContainer: {
    overflowX: "auto",
    marginTop: "20px",
    borderRadius: "8px",
    border: "1px solid #e0e0e0",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: "400px",
  },
  searchBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
    gap: "20px",
  },
  searchContainer: {
    flex: 1,
    position: "relative",
  },
  searchIcon: {
    position: "absolute",
    left: "12px",
    top: "50%",
    transform: "translateY(-50%)",
    color: "#666",
  },
  searchInput: {
    width: "100%",
    padding: "12px 12px 12px 40px",
    borderRadius: "8px",
    border: "1px solid #ddd",
    fontSize: "14px",
    boxSizing: "border-box",
  },
  statisticsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(1, 1fr)",
    gap: "20px",
    marginBottom: "20px",
  },
  statCard: {
    background: "white",
    padding: "20px",
    borderRadius: "12px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    display: "flex",
    alignItems: "center",
    gap: "15px",
  },
  statIcon: {
    fontSize: "22px",
    color: "#007bff",
  },
  statValue: {
    margin: 0,
    fontSize: "22px",
    fontWeight: "700",
    color: "#333",
  },
  statLabel: {
    margin: "5px 0 0 0",
    color: "#666",
    fontSize: "14px",
  },
  button: {
    background: "#333",
    color: "white",
    padding: "8px 24px",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    cursor: "pointer",
  },
  actionBTN: {
    display: "flex",
    gap: "8px",
  },
  editButton: {
    backgroundColor: "#e8f5e9",
    color: "#388e3c",
    border: "none",
    borderRadius: "6px",
    padding: "8px 12px",
    cursor: "pointer",
  },
  deleteButton: {
    backgroundColor: "#ffebee",
    color: "#d32f2f",
    border: "none",
    borderRadius: "6px",
    padding: "8px 12px",
    cursor: "pointer",
  },
  pagination: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "20px",
    paddingTop: "20px",
    borderTop: "1px solid #e0e0e0",
  },
  paginationButton: {
    background: "#f8f9fa",
    border: "1px solid #dee2e6",
    padding: "8px 16px",
    borderRadius: "6px",
    cursor: "pointer",
  },
  pageNumbers: {
    display: "flex",
    gap: "8px",
  },
  pageButton: {
    background: "white",
    border: "1px solid #dee2e6",
    padding: "8px 12px",
    borderRadius: "6px",
    cursor: "pointer",
    minWidth: "40px",
  },
  activePageButton: {
    background: "#007bff",
    color: "white",
    borderColor: "#007bff",
  },
  pageInfo: {
    color: "#666",
    fontSize: "14px",
  },
  categoryNameCell: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  categoryIcon: {
    color: "#007bff",
    fontSize: "16px",
  },
};
